import { useEffect, useState } from 'react'
import { AdasBox, AdasContainer } from '@components/wrapper-components'
import { Settings, Plus } from 'lucide-react'
import { ScheduleCalendar, MobileScheduler, SchedulerSetupPrompt } from '@components/scheduler'
import { useHistory } from 'react-router-dom'
import { Logger } from '../../logger'
import { getOrganisationByReferenceId, setupOrganisationByCCId } from '../../api/api'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { useCenterStore, useOrganisationStore, useLoadingStore, useMessageStore } from '@caradasstore'
import { CreateAppointmentForm } from '@components/scheduler/schedule-calendar/CreateAppointmentForm'
import { AppointmentDetails } from '@components/scheduler/schedule-calendar/AppointmentDetails'
import { schedularStatusStyles, statusOptions } from '@components/scheduler/schedule-calendar/helper'
import { useUserStore } from '@caradasstore/UserStore'
import { usePolling } from 'utils/usePolling'
import { SCHEDULER_AUTO_REFRESH_MINUTES } from 'constants'
import { useDeviceQueries } from '@hooks/useDeviceQueries'

export const SchedulerPage = () => {
  const history = useHistory()
  const ccId = useCenterStore((store) => store.ccId)
  const setOrganisationDetail = useOrganisationStore((store) => store.setOrganisationDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [showOrganisationSetup, setShowOrganisationSetup] = useState(false)
  const [showDefaultPage, setShowDefaultPage] = useState(true)
  const currentUser = useUserStore((store) => store.currentUser)
  const addMessage = useMessageStore((store) => store.addMessage)
  const { isMobile, isLandscape } = useDeviceQueries()

  const [newEvent, setNewEvent] = useState(false)
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [eventCount, setEventCount] = useState(0)
  const [visibleCount, setVisibleCount] = useState(0)
  const isMobilePortrait = isMobile && !isLandscape
  const initialView = isMobilePortrait ? 'timeGridDay' : 'timeGridWeek'

  const redirectToCalendar = () => {
    history.push('/calendar')
  }
  const [refresh, setRefresh] = useState(false)

  usePolling(setRefresh, refresh, SCHEDULER_AUTO_REFRESH_MINUTES)

  useEffect(() => {
    const fetchOrganisationDetailsByCcId = async () => {
      setLoading(true)
      const orgResponse = await getOrganisationByReferenceId({ ccId: ccId })
      setLoading(false)
      if (orgResponse?.status === 200) {
        if (orgResponse?.data.id) {
          setOrganisationDetail(orgResponse.data)
          setShowDefaultPage(false)
        } else if (orgResponse?.data?.id === null) {
          setShowOrganisationSetup(true)
        }
      } else {
        Logger.error({
          message: 'Failed to fetch organisation data',
          payload: { file: 'calendarpage', method: 'getOrganisationByReferenceId' }
        })
      }
    }
    if (ccId) {
      fetchOrganisationDetailsByCcId(ccId)
    }
  }, [ccId, setOrganisationDetail, setShowOrganisationSetup, setShowDefaultPage])

  // CAR-3634: one friendly, mapped message — never raw server text.
  const handleCustomError = (error) => {
    addMessage({ text: getFriendlyErrorMessage(error) })
  }

  const handleSetupOrganisationConfirm = async () => {
    try {
      setLoading(true)
      const res = await setupOrganisationByCCId(ccId)
      setLoading(false)
      if (res?.status === 200) {
        setOrganisationDetail(res.data)
        setShowDefaultPage(false)
      }
    } catch (error) {
      Logger.error({
        message: 'Failed to setup organisation',
        payload: { file: 'calendarpage', method: 'setupOrganisationByCCId' }
      })
      handleCustomError(error)
    } finally {
      setLoading(false)
    }
  }

  const canEditSettings = () => {
    return (currentUser && currentUser.is_owner) || currentUser.is_manager || currentUser.is_admin
  }

  const openNewEventSlider = () => {
    setSelectedEventId(null)
    setNewEvent(true)
  }

  const handleEventOpen = (eventId) => {
    setNewEvent(false)
    setSelectedEventId(eventId)
  }

  const closeAppointmentDetails = () => setSelectedEventId(null)

  return (
    <AdasContainer maxWidth={false} className='pb-[10px]'>
      {!showDefaultPage && isMobilePortrait && (
        <>
          <MobileScheduler refresh={refresh} openNewEventSlider={openNewEventSlider} onEventOpen={handleEventOpen} />

          {newEvent && <CreateAppointmentForm open={newEvent} onClose={() => setNewEvent(false)} setRefresh={setRefresh} />}

          {selectedEventId && (
            <AppointmentDetails
              key={selectedEventId}
              isOpen={!!selectedEventId}
              selectedEventId={selectedEventId}
              cancel={closeAppointmentDetails}
              setRefresh={setRefresh}
            />
          )}
        </>
      )}

      {!showDefaultPage && !isMobilePortrait && (
        <>
          <AdasBox className='pt-6 md:pt-4 mb-5 flex flex-wrap items-center justify-between gap-3'>
            <div className='flex items-baseline gap-3 flex-wrap'>
              <h1 className='text-[32px] font-bold leading-tight tracking-tight text-[#0A1628]'>Appointment Schedule</h1>
            </div>
            <div className='flex items-center gap-3'>
              {canEditSettings() && (
                <button
                  type='button'
                  onClick={redirectToCalendar}
                  className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Settings className='h-4 w-4' />
                  Calendar Settings
                </button>
              )}
              {/* Booking is the one write a client user may make, so this is offered to every role. */}
              <button
                type='button'
                onClick={openNewEventSlider}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
              >
                <Plus className='h-4 w-4' />
                New Appointment
              </button>
            </div>
          </AdasBox>

          <div className='w-full'>
            <ScheduleCalendar
              initialView={initialView}
              refresh={refresh}
              openNewEventSlider={openNewEventSlider}
              onEventOpen={handleEventOpen}
              onCountChange={(total, visible) => {
                setEventCount(total)
                setVisibleCount(visible)
              }}
            />
          </div>

          {newEvent && <CreateAppointmentForm open={newEvent} onClose={() => setNewEvent(false)} setRefresh={setRefresh} />}

          {selectedEventId && (
            <AppointmentDetails
              key={selectedEventId}
              isOpen={!!selectedEventId}
              selectedEventId={selectedEventId}
              cancel={closeAppointmentDetails}
              setRefresh={setRefresh}
            />
          )}

          <AdasBox className='sticky bottom-0 z-20 mt-4 -mx-3 min-[435px]:-mx-6 px-3 min-[435px]:px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-[12px] text-[#6B7280] bg-white border-t border-[#EBEDF0]'>
            <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
              {statusOptions.map((s) => (
                <span key={s.id} className='inline-flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: schedularStatusStyles[s.id]?.borderColor || '#9CA3AF' }}
                  />
                  {s.name}
                </span>
              ))}
            </div>
            <div>
              Showing {visibleCount} of {eventCount} appointments
            </div>
          </AdasBox>
        </>
      )}

      {showDefaultPage && (
        <AdasBox
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          gap={2}
          className='overflow-hidden min-h-[81vh]'
        >
          {showOrganisationSetup && <SchedulerSetupPrompt currentUser={currentUser} onSetup={handleSetupOrganisationConfirm} />}
        </AdasBox>
      )}
    </AdasContainer>
  )
}
